import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { formatMoney } from '../../utils/format';
import {
  listIndirectItems, createIndirectItem, updateIndirectItem, deleteIndirectItem,
  setIndirectAllocation,
} from '../../api/indirectItems';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import { useRegisterPageExports } from '../../exports/pageExports';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import '../hr/HrPage.css';
import './IndirectCosts.css';

/* Charges indirectes — la saisie des charges d'exploitation HORS PERSONNEL.

   Elles se résumaient à un seul montant annuel, saisi dans la cascade CCR sous
   le nom de « charge indirecte hypothétique » : un chiffre sans composition,
   que personne ne pouvait vérifier six mois plus tard. On les détaille ici,
   poste par poste.

   Le détail est la SAISIE, le montant annuel est le RÉSULTAT : le serveur le
   recompose à chaque écriture, donc la base qui pilote le CCR ne peut pas
   s'écarter de ce qu'on lit à l'écran. Sans aucune ligne, le montant unique
   saisi avant reste en place — supprimer la dernière ligne ne remet pas une
   base à zéro en silence.

   On saisit le TOTAL de la ligne, puis, quand la charge appartient à un
   groupe (loyer d'un atelier, licence métier, véhicule), la part qui lui
   revient. Ce qui reste est COMMUN et se répartit sur la capacité pratique :
   la colonne se remplit d'elle-même, elle ne se saisit pas. On tape le montant
   qu'on a sous les yeux, le découpage vient après (2026-08-30). Le
   serveur recompose depuis ces parts les « charges spécifiques » que la
   cascade CCR retranche du solde à répartir : elles se saisissaient jusque-là
   dans « Affectation frais », loin de la ligne qu'elles découpent.

   Le coût du PERSONNEL n'entre pas ici : il vient de People et rejoint la
   cascade par un autre chemin. */
export default function IndirectCostsPage({ activeTenant, addToast, year, embedded = false }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;

  const [items, setItems] = useState([]);
  const [pools, setPools] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const viewRef = React.useRef(null);
  const excel = useViewExcelExport({
    ref: viewRef,
    sheetName: 'Charges indirectes',
    fileBase: 'Charges indirectes',
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  useRegisterPageExports(useMemo(() => ([
    { id: 'excel', label: 'Excel', run: excel.run },
  ]), [excel.run]));

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    const r = await listIndirectItems(tenantId, year);
    if (!r.error) {
      setItems(r.data?.items || []);
      /* Les groupes PROTÉGÉS (structure/overhead) ne portent pas de charge
         spécifique : leur coût est redistribué, pas alloué. */
      setPools((r.data?.pools || []).filter(x => !x.is_protected));
      setTotal(Number(r.data?.total || 0));
    }
    setLoading(false);
  }, [tenantId, year]);

  useEffect(() => { reload(); }, [reload]);

  const fail = (r) => {
    if (r?.error && addToast) addToast(r.error, 'error');
    return Boolean(r?.error);
  };

  async function addLine() {
    setBusy(true);
    const r = await createIndirectItem(tenantId, year, { label: p('Nouveau poste'), amount: 0 });
    setBusy(false);
    if (!fail(r)) reload();
  }

  async function saveLine(id, patch) {
    const r = await updateIndirectItem(tenantId, id, patch);
    if (!fail(r)) reload();
  }

  async function saveAlloc(itemId, poolId, val) {
    const r = await setIndirectAllocation(tenantId, itemId, poolId, val);
    if (!fail(r)) reload();
  }

  async function removeLine(id, label) {
    if (!window.confirm(p('Supprimer « {{label}} » ?', { label }))) return;
    const r = await deleteIndirectItem(tenantId, id);
    if (!fail(r)) reload();
  }

  if (!tenantId) {
    return (
      <PageShell embedded={embedded} framed={!embedded} title={p('Charges indirectes')}>
        <div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div>
      </PageShell>
    );
  }

  return (
    <PageShell embedded={embedded} framed={!embedded} title={p('Charges indirectes')} exportRef={viewRef}>
      <main className="hr-page is-embedded">
        {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
        {!loading && (
          <CardShell
            flush
            title={p('Charges indirectes')}
            subtitle={`${p("Charges d'exploitation hors personnel")} · ${year}`}
            primaryAction={(
              <button type="button" className="lk-btn" disabled={busy} onClick={addLine}>
                ＋ {p('Ajouter un poste')}
              </button>
            )}
          >
            <div className="hr-page__table-wrap" style={{ overflowX: 'auto' }}>
            <table className="hr-page__table ic-table">
              <thead>
                <tr>
                  <th>{p('Désignation')}</th>
                  <th className="hr-page__num">{p('Total')}</th>
                  <th className="hr-page__num hr-page__col-sep" title={p('Solde : ce que le découpage par groupe n’a pas pris. Se répartit sur la capacité pratique.')}>
                    {p('Commun')}
                  </th>
                  {pools.map((pool) => (
                    <th key={pool.id} className="hr-page__num ic-th-pool"
                        title={p('Part de cette charge qui revient au groupe {{name}}', { name: pool.name })}>
                      {pool.name}
                    </th>
                  ))}
                  <th className="ic-th-act" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={pools.length + 4} className="hr-page__empty">
                      {p('Aucun poste saisi.')}
                    </td>
                  </tr>
                )}
                {items.map((it) => {
                  const alloc = it.allocations || {};
                  const common = Number(it.common ?? 0);
                  return (
                    <tr key={it.id}>
                      <td>
                        <LabelCell value={it.label} onSave={(v) => saveLine(it.id, { label: v })} />
                      </td>
                      <td className="hr-page__num">
                        <AmountCell value={Number(it.amount || 0)} onSave={(v) => saveLine(it.id, { amount: v })} />
                      </td>
                      {/* Le commun est un SOLDE : négatif, il dit que le
                          découpage dépasse le total, et se voit plutôt que de
                          se laisser ramener à zéro. */}
                      <td className={`hr-page__num hr-page__col-sep${common < 0 ? ' ic-over' : ' hr-page__muted'}`}
                          title={common < 0 ? p('Le découpage par groupe dépasse le total de la ligne.') : undefined}>
                        {formatMoney(common, 'chf', 0)}
                      </td>
                      {pools.map((pool) => (
                        <td key={pool.id} className="hr-page__num">
                          <AmountCell value={Number(alloc[pool.id] || 0)}
                                      onSave={(v) => saveAlloc(it.id, pool.id, v)} />
                        </td>
                      ))}
                      <td className="ic-td-act">
                        <button type="button" className="ic-del" title={p('Supprimer')}
                                onClick={() => removeLine(it.id, it.label)}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="hr-page__total-row">
                  <td><strong>{p('Total des charges indirectes')}</strong></td>
                  <td className="hr-page__num"><strong>{formatMoney(total, 'chf', 0)}</strong></td>
                  <td className="hr-page__num hr-page__col-sep">
                    <strong>{formatMoney(items.reduce((a, it) => a + Number(it.common ?? 0), 0), 'chf', 0)}</strong>
                  </td>
                  {pools.map((pool) => (
                    <td key={pool.id} className="hr-page__num">
                      <strong>{formatMoney(
                        items.reduce((a, it) => a + Number((it.allocations || {})[pool.id] || 0), 0),
                        'chf', 0)}</strong>
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            </table>
            </div>
          </CardShell>
        )}
      </main>
    </PageShell>
  );
}

/* Cellules éditables — texte en bleu de saisie, sans cadre (charte) : on clique
   la valeur, on tape, on quitte le champ pour enregistrer. */
function LabelCell({ value, onSave }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <input
      className="ic-input"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft.trim() && draft !== value) onSave(draft.trim()); else setDraft(value); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

/* Le montant se LIT avec ses milliers séparés et s'ÉDITE en chiffres nus :
   taper par-dessus des apostrophes est pénible, les relire sans elles l'est
   tout autant. Le champ bascule au focus. */
const groupCHF = (n) => Math.round(Number(n) || 0).toLocaleString('de-CH').replace(/\./g, '’');
const plain = (n) => String(Math.round(Number(n) || 0));

function AmountCell({ value, onSave }) {
  const [draft, setDraft] = useState(groupCHF(value));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(groupCHF(value)); }, [value, editing]);
  return (
    <input
      className="ic-input ic-input--num"
      value={draft}
      inputMode="decimal"
      onFocus={() => { setEditing(true); setDraft(value ? plain(value) : ''); }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const typed = Number(String(draft).replace(/[\s'’]/g, '').replace(',', '.'));
        setEditing(false);
        if (Number.isFinite(typed) && typed >= 0 && Math.abs(typed - value) >= 0.5) onSave(typed);
        else setDraft(groupCHF(value));
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}
